<script setup lang="ts">
import {computed, onMounted, onUnmounted, type PropType, ref} from 'vue';
import {useRoute} from 'vue-router';
import {type NavItem} from '@/views/shell/navItems.js';
import {CabMe} from 'common';
import {EyeOff} from 'lucide-vue-next';
import StatusTag from '@/views/ui/StatusTag.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {clearMe, getMeInfo} from '@/base/me.js';
import {stopImpersoneren} from '@/base/callBackend';

const props = defineProps({
    landing: {type: String, required: true},
    items  : {type: Array as PropType<NavItem[]>, required: true},
});

const route = useRoute();
const me    = ref<CabMe | undefined>(undefined);
const tab   = computed<string>(() => props.items.find(i => i.exact === false ? route.path.startsWith(i.to) : route.path === i.to)?.label ?? '');

// the height is published as a css var so that sticky table headers can stick just below this bar
const barRef = ref<HTMLElement>();
let resizeObserver: ResizeObserver | undefined;

onMounted(async () => {
    me.value = await getMeInfo();
    resizeObserver = new ResizeObserver(() =>
        document.documentElement.style.setProperty('--mainheader-height', `${barRef.value?.offsetHeight ?? 0}px`));
    resizeObserver.observe(barRef.value!);
});

onUnmounted(() => resizeObserver?.disconnect());

async function onStop() {
    await stopImpersoneren();
    clearMe();
    window.location.assign('/');
}
</script>

<template>
    <header ref="barRef" id="mainheader" class="sticky top-0 z-40 bg-white border-b border-zzc-rand flex flex-row flex-wrap items-center gap-x-4 gap-y-2 px-5 min-h-12">
        <div class="text-sm text-zzc-muted whitespace-nowrap">
            {{ landing }}<template v-if="tab"> &rsaquo; <span class="text-zzc-antraciet font-medium">{{ tab }}</span></template>
        </div>
        <div class="flex flex-row flex-wrap items-center gap-3 ml-auto">
            <div v-if="me?.echtEmail" class="flex items-center gap-1" data-testid="impersonatie-banner">
                <StatusTag kind="warn">je bekijkt als {{ me.email }}</StatusTag>
                <IconButton :icon="EyeOff" label="stop impersoneren" size="sm" data-testid="stop-impersoneren" @click="onStop"/>
            </div>
            <slot/>
        </div>
    </header>
</template>
